import React, { useContext, useCallback, useMemo } from 'react';
import { useAppSelector } from 'flow/hooks/useRedux';
import { Set } from 'immutable';
import { stringStartsWithHttps } from 'flow/utils';
import { applyLink, getProxyImageUrl } from 'flow/utils/message-utils';
import AppContext from 'flow/contexts/app-context';

import { TypedMap } from 'flow/types/TypedMap';
import { Message } from 'flow/types/Chats';
import { twMerge } from 'tailwind-merge';

interface OpenGraphImageProps {
    imageUrl: string | null;
    cbOpenGraph: () => void;
}

const OpenGraphImage = ({ imageUrl, cbOpenGraph }: OpenGraphImageProps) => {
    if (!imageUrl) return null;

    return (
        <div className="max-h-[210px] max-w-[300px] overflow-hidden leading-none">
            <img
                onLoad={cbOpenGraph}
                className="h-auto w-full cursor-pointer"
                src={imageUrl}
                alt="Open Graph"
                height={200}
                width={300}
            />
        </div>
    );
};

interface OpenGraphTextBoxProps {
    description?: string;
    title: string;
    siteName?: string;
}

const OpenGraphTextBox = ({
    description,
    title,
    siteName,
}: OpenGraphTextBoxProps) => (
    <div className="px-2 py-3">
        <div className="mb-2 line-clamp-2 text-sm font-bold text-white">
            {title}
        </div>
        {description && (
            <div className="line-clamp-2 text-xs text-gray-300">
                {description}
            </div>
        )}
        {siteName && (
            <div
                className={twMerge(
                    'text-xs text-gray-400',
                    description ? 'mt-3' : '',
                )}
            >
                {siteName}
            </div>
        )}
    </div>
);

interface MessageOpenGraphItemProps {
    message: TypedMap<Message>;
    cbOpenGraph?: () => void;
}

const MessageOpenGraphItem = ({
    message,
    cbOpenGraph,
}: MessageOpenGraphItemProps) => {
    const { i18next } = useContext(AppContext);

    const openGraphData = useAppSelector((state) => state.openGraph);

    const isLink = (
        messagePart: any,
    ): messagePart is React.ReactElement<{ href: string }> =>
        messagePart instanceof Object && messagePart.props?.href;

    const getMessageUrls = (message: string) => {
        const messageParts = Set(applyLink(message));
        return messageParts
            .filter(isLink)
            .map((messagePart: any) => messagePart.props.href);
    };

    const createOpenGraphItem = useCallback(
        (url: string) => {
            const openGraphDTO = openGraphData.get(url);

            if (openGraphDTO?.isEmpty()) {
                return null;
            }

            const description = openGraphDTO?.get('description');
            const imageUrl = openGraphDTO?.get('image');
            const imageUrlStartsWithHttps = stringStartsWithHttps(imageUrl);
            const proxyUrl = getProxyImageUrl(imageUrl);

            if (imageUrlStartsWithHttps || description) {
                const title =
                    openGraphDTO?.get('title') ||
                    i18next.t('opengraph.title.unavailable');
                const siteName = openGraphDTO?.get('siteName');

                return (
                    // The link is the card, so the card's clipping sits on
                    // the link: a wrapper would clip its focus ring.
                    <div className="mt-2" key={url}>
                        <a
                            className="block max-w-[300px] overflow-hidden rounded-lg bg-gray-800 no-underline"
                            target="_blank"
                            rel="nofollow noopener noreferrer"
                            href={url}
                        >
                            {imageUrlStartsWithHttps && (
                                <OpenGraphImage
                                    cbOpenGraph={cbOpenGraph}
                                    imageUrl={proxyUrl}
                                />
                            )}
                            <OpenGraphTextBox
                                title={title}
                                description={description}
                                siteName={siteName}
                            />
                        </a>
                    </div>
                );
            }

            return null;
        },
        [openGraphData],
    );

    const urls = useMemo(
        () => getMessageUrls(message.get('message')),
        [message],
    );

    if (!openGraphData || urls.isEmpty()) return null;

    return <>{urls.map(createOpenGraphItem)}</>;
};

export default React.memo(MessageOpenGraphItem);
